import { Dispatch, SetStateAction, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import z from "zod";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiActions,
  MiBox,
  MiButton,
  MiDetailFields,
  MiList,
  MiMenu,
  MiModalConfirm,
  MiSectionHeader,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
} from "@miplatform/constants";
import { IBidOptionResponse, IOptionPricingResponse } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiPencil, mdiPlus, mdiTrashCan, mdiTrashCanOutline } from "@mdi/js";
import { Box } from "@mui/material";
import { useCreateBidContext } from "../../../CreateBidContext";
import { BID_STATUS, DEFAULT_OPTION } from "../../../constants";
import { OptionCard } from "../OptionCard/OptionCard";
import { OptionDetails } from "../OptionDetails/OptionDetails";

const schema = z.object({
  name: z.string().min(1, "Option name is required"),
});

export function OptionsSection({
  open,
  optionData,
  optionPricing,
  isEditingOption,
  selectedOption,
  setOptionData,
  setSelectedOption,
  getOption,
  handleCreateOption,
  handleUpdateOption,
  handleDeleteOption,
  handleCancelOptionEdit,
}: OptionsSectionProps) {
  const [openOptionNameForm, setOpenOptionNameForm] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { form } = useCreateBidContext();
  const { watch } = form;
  const { options = [], planStatus } = watch();
  const isBidApproved = planStatus === BID_STATUS.APPROVED;

  const sortedOptions = [...options].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );

  const {
    control,
    getValues,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: { name: "" },
    resolver: zodResolver(schema),
  });
  const { classes, theme } = useStyles();

  if (!open) {
    return null;
  }

  const handleStartEditOption = async (option: IBidOptionResponse) => {
    setSelectedOption(option);
    await getOption(option);
  };

  const handleChangeOptionName = (name: string) => {
    setOptionData((prev) => ({ ...prev, name }));
    setSelectedOption((prev) => (prev ? { ...prev, name } : prev));
  };

  const handleResetOption = () => {
    setOptionData(DEFAULT_OPTION);
    setSelectedOption(null);
  };

  const handleCloseNameForm = () => {
    setOpenOptionNameForm(false);
    setSelectedOption(null);
    reset({ name: "" });
  };

  const handleContinue = () => {
    const { name } = getValues();
    handleCreateOption(name);
    setOpenOptionNameForm(false);
  };

  const renderActions = (option: IBidOptionResponse) => (
    <Box onClick={(e) => e.stopPropagation()}>
      <MiMenu
        data={[
          {
            label: BUTTON_LABELS.EDIT,
            icon: mdiPencil,
            action: () => handleStartEditOption(option),
          },
          {
            label: BUTTON_LABELS.DELETE,
            icon: mdiTrashCanOutline,
            color: theme.palette.red.main,
            action: () => {
              setSelectedOption(option);
              setOpenDeleteModal(true);
            },
          },
        ]}
      />
    </Box>
  );

  const renderOptionsList = () => (
    <>
      <MiSectionHeader
        title="Options"
        subtitle="Add and/or remove items or areas on the bid to begin creating your option."
        headerStyle={{ backgroundColor: theme.palette.grey.light }}
        rightAction={
          <MiButton
            title="Option"
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{ path: mdiPlus }}
            disabled={isBidApproved}
            onClick={() => {
              reset({ name: "" });
              setSelectedOption(null);
              setOpenOptionNameForm(true);
            }}
          />
        }
      />
      <MiList
        viewType="card"
        data={sortedOptions}
        hideListPagination
        defaultPageSize={100}
        CardComponent={OptionCard}
        cardProps={{ renderActions, optionPricing }}
        emptyListProps={{
          text: "No Options Created",
          helperText: "Tip: To create, click the + Option button",
          image: MiBuildHouseAlt,
          iconBgColor: theme.palette.primary.light,
          containerStyle: {
            backgroundColor: theme.palette.primary.light,
            margin: theme.spacing(1),
            borderRadius: 4,
          },
        }}
      />
    </>
  );

  const renderNameForm = () => (
    <MiDetailFields
      detailFields={[{ name: "name", label: "Name", formControl: control }]}
      headerProps={{
        title: "Create Option",
        variant: "text",
        rightAction: (
          <Box className={classes.actionBox}>
            <MiActions
              actions={[
                {
                  name: "Cancel",
                  onClick: handleCloseNameForm,
                  variant: BUTTON_VARIANTS.TEXT,
                  color: BUTTON_COLORS.BLACK,
                },
                {
                  name: "Continue",
                  onClick: handleContinue,
                  variant: BUTTON_VARIANTS.CONTAINED,
                  disabled: !isValid,
                },
              ]}
            />
          </Box>
        ),
      }}
    />
  );

  const renderContent = () => {
    if (isEditingOption) {
      return (
        <OptionDetails
          option={optionData}
          handleChangeOptionName={handleChangeOptionName}
          handleUpdateOption={() => handleUpdateOption(optionData.name)}
          handleCancelOptionEdit={() => {
            handleCancelOptionEdit();
            handleResetOption();
          }}
        />
      );
    }
    if (openOptionNameForm) {
      return renderNameForm();
    }
    return renderOptionsList();
  };

  return (
    <MiBox className={classes.container}>
      {renderContent()}
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        titleIcon={{ path: mdiTrashCan, color: theme.palette.red.main }}
        handleNegative={() => {
          setOpenDeleteModal(false);
          setSelectedOption(null);
        }}
        handlePositive={() => {
          if (selectedOption) {
            handleDeleteOption(selectedOption);
          }
          setOpenDeleteModal(false);
        }}
        description={
          <>
            You are about to delete option{" "}
            <span className={classes.boldText}>{selectedOption?.name}</span>.
            Are you sure you would like to continue?
          </>
        }
      />
    </MiBox>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey.light,
    borderRadius: 2,
  },
  actionBox: {
    display: "flex",
    gap: theme.spacing(1),
  },
  boldText: {
    fontWeight: theme.fontWeight.semiBold,
    color: theme.palette.primary.main,
  },
}));

interface OptionsSectionProps {
  open: boolean;
  optionData: IBidOptionResponse;
  optionPricing: IOptionPricingResponse[];
  isEditingOption: boolean;
  selectedOption: IBidOptionResponse | null;
  setOptionData: Dispatch<SetStateAction<IBidOptionResponse>>;
  setSelectedOption: Dispatch<SetStateAction<IBidOptionResponse | null>>;
  getOption: (option: IBidOptionResponse) => Promise<void>;
  handleCreateOption: (name: string) => void;
  handleUpdateOption: (name: string) => void;
  handleDeleteOption: (option: IBidOptionResponse) => void;
  handleCancelOptionEdit: () => void;
}
